import { useState } from "react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  "Founder",
  "CTO",
  "Senior Developer",
  "UI/UX Designer",
  "Biz Dev Lead",
  "Marketing Manager",
  "Community Manager",
  "Customer Success Rep",
  "Sales Representative",
  "Operations Director",
  "Support Staff",
  "General Interest",
];

const inputCls =
  "w-full border border-line bg-white px-4 py-3.5 text-sm text-charcoal-900 placeholder:text-slate-400 outline-none transition-all duration-200 focus:border-accent focus:ring-2 focus:ring-accent/15";

const ApplicationForm = () => {
  const [form, setForm] = useState({ name: "", email: "", role: ROLES[0], link: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/application`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      toast.success("Application received. The team will review it and reach out.");
      setForm({ name: "", email: "", role: ROLES[0], link: "", message: "" });
    } catch {
      toast.error("Something went wrong. Email us at hello@truelifeentertainment.com");
    } finally {
      setSending(false);
    }
  };

  return (
    <form data-testid="application-form" onSubmit={submit} className="border border-line bg-white p-7 sm:p-9">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="ap-name" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-steel">Full name</label>
          <input id="ap-name" data-testid="application-name-input" className={inputCls} placeholder="Your name" value={form.name} onChange={update("name")} required />
        </div>
        <div>
          <label htmlFor="ap-email" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-steel">Email</label>
          <input id="ap-email" type="email" data-testid="application-email-input" className={inputCls} placeholder="you@email.com" value={form.email} onChange={update("email")} required />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="ap-role" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-steel">Role</label>
        <select id="ap-role" data-testid="application-role-select" className={`${inputCls} appearance-none`} value={form.role} onChange={update("role")}>
          {ROLES.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="ap-link" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-steel">
          LinkedIn / Portfolio <span className="text-slate-400">(optional)</span>
        </label>
        <input id="ap-link" data-testid="application-link-input" className={inputCls} placeholder="https://linkedin.com/in/..." value={form.link} onChange={update("link")} />
      </div>

      <div className="mt-4">
        <label htmlFor="ap-message" className="mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-steel">About you</label>
        <textarea id="ap-message" data-testid="application-message-input" rows={5} className={`${inputCls} resize-none`} placeholder="Tell us about your experience and the role you're after..." value={form.message} onChange={update("message")} required />
      </div>

      <button
        type="submit"
        data-testid="application-submit-button"
        disabled={sending}
        className="mt-6 w-full bg-accent py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-200 hover:bg-accent-dark disabled:opacity-60"
      >
        {sending ? "Submitting..." : "Submit Application"}
      </button>
    </form>
  );
};

export default ApplicationForm;
